<template>
  <BLayoutList
    :title="pageTitle"
    :create-route="createRoute"
    :skeleton="isLoadingAsyncData"
  >
    <template v-if="canEdit" #before-add>
      <BMenuMore
        v-if="selected.length"
        :list="menuSelected"
        class="mr-2"
        @selected="onSelectSelectedMenu"
      />

      <BMenuMore
        :list="menuImport"
        :skeleton="isLoadingAsyncData"
        class="mr-2"
        @selected="onSelectImportMenu"
      >
        <template #activator>
          <v-btn depressed class="pr-1">
            <span>Импорт</span>
            <v-divider vertical class="ml-2 mr-1" />
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
      </BMenuMore>

      <BMenuMore
        :list="menuExport"
        :skeleton="isLoadingAsyncData"
        class="mr-2"
        @selected="onSelectExportMenu"
      >
        <template #activator>
          <v-btn depressed class="pr-1">
            <span>Экспорт</span>
            <v-divider vertical class="ml-2 mr-1" />
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
      </BMenuMore>
    </template>

    <template #filter>
      <ComponentFilter
        :data="filters"
        :skeleton="isLoadingAsyncData"
        @change="onChangeFilter"
      />
    </template>

    <BTableCommon
      v-model="selectedValue"
      type="users"
      :list="list"
      :actions="actions"
      :pagination="pagination"
      :ordering="filters.ordering"
      :loading="isLoading"
      :skeleton="isLoadingAsyncData"
      @change:pagination="onChangePagination"
      @change:sort="onChangeSort"
      @action:delete="onDeleteWithRefresh"
      @action:su-as="onSuAs"
    />
  </BLayoutList>
</template>

<script>
import { authUserPermissions as Permissions } from '@/helpers/enums/permission-models'
import { createListMixin } from '@/helpers/mixins/createListMixin'
import { UserModel as Model } from './Model'
import {
  usersStoreModule as StoreModule,
  usersMapper as StoreMapper,
} from './store'
import {
  USERS_ROUTES as ROUTES,
  createUsersModalChangeStatus,
  createUploadPermissionsFile,
  createUploadUsersFile,
  createUploadUsersActivityFile,
  createUploadPermissionGroupsFile,
  createExportUsersActivityModal,
} from './utils'
import { createTableActions } from './table-headers'

import BLayoutList from '@/components/layouts/BLayoutList.vue'
import BTableCommon from '@/components/tables/BTableCommon.vue'
import BMenuMore from '@/components/common/BMenuMore.vue'
import ComponentFilter from './components/UsersFilter.vue'
import { AuthServiceApi } from '@/api/resources'
import { authStoreMapper } from '@/views/Auth/store'


const mixinOptions = { Model, StoreModule }
const listMixin = createListMixin(mixinOptions)

const MENU_SELECTED = {
  STATUS: { id: 'status', title: 'Изменить статус' },
  DELETE: { id: 'delete', title: 'Удалить выбранные' },
}

const MENU_IMPORT = {
  PERMISSION_ROADMAP: { id: 'permission_roadmap', title: 'Импорт прав ДК' },
  IMPORT_USERS: { id: 'import_users', title: 'Импорт пользователей' },
  USERS_ACTIVITY: { id: 'import_users_activity', title: 'Обновление активности пользователей' },
  IMPORT_PERMISSION_GROUPS: { id: 'import_permission_groups', title: 'Импорт групп с правами' },
}

const MENU_EXPORT = {
  USERS_ACTIVITY: { id: 'export_users_activity', title: 'Активность пользователей' },
}

/** @typedef { import('@/helpers/mixins/createListMixin').ListMixin<any, StoreModule> } List */

export default {
  name: 'ViewUsersAll',
  components: {
    BLayoutList,
    BTableCommon,
    BMenuMore,
    ComponentFilter,
  },
  mixins: [
    listMixin,
  ],
  data: () => ({
    selected: [],
    menuImport: Object.values(MENU_IMPORT),
    menuSelected: Object.values(MENU_SELECTED),
    menuExport: Object.values(MENU_EXPORT),
  }),
  computed: {
    ...authStoreMapper.mapGetters(['profile']),
    canEdit() {
      return this.$access(Permissions.edit)
    },
    actions() {
      if (!this.canEdit) return []
      return createTableActions(this.profile)
    },
    selectedValue: {
      get() {
        return this.canEdit ? this.selected : void 0
      },
      set(val) {
        this.selected = val
      },
    },
    createRoute() {
      const editable = this.$access(Permissions.create)
      return editable ? { name: ROUTES.CREATE } : void 0
    },
  },
  /** @type { List } */
  methods: {
    ...StoreMapper.mapActions(['updateActive']),

    async onSelectSelectedMenu(data) {
      switch (data.id) {
        case MENU_SELECTED.STATUS.id:
          await this.toChangeStatus(this.selected)
          this.onGetData(this.filters, false)
          break

        case MENU_SELECTED.DELETE.id:
          await this.onDeleteMultipleWithRefresh(this.selected)
          break

        default:
      }

      this.selected = []
    },
    async toChangeStatus(selected) {
      const propsData = { action: this.updateActive, users: selected }
      const modal = await createUsersModalChangeStatus(this, propsData)
      return modal.open()
    },
    async onSelectImportMenu(data) {
      switch (data.id) {
        case MENU_IMPORT.PERMISSION_ROADMAP.id: {
          const flag = await this.toUploadPermissionsFile()
          if (flag) this.onGetData(this.filters, false)
          break
        }

        case MENU_IMPORT.IMPORT_USERS.id: {
          const flag = await this.toUploadUsersFile()
          if (flag) this.onGetData(this.filters, false)
          break
        }

        case MENU_IMPORT.USERS_ACTIVITY.id: {
          const flag = await this.toUploadUsersActivityFile()
          if (flag) this.onGetData(this.filters, false)
          break
        }

        case MENU_IMPORT.IMPORT_PERMISSION_GROUPS.id: {
          const flag = await this.toUploadPermissionGroupsFile()
          if (flag) this.onGetData(this.filters, false)
          break
        }


        default:
      }
    },
    async toUploadPermissionsFile() {
      const modal = await createUploadPermissionsFile(this)
      return modal.open()
    },

    async toUploadUsersFile() {
      const modal = await createUploadUsersFile(this)
      const result = await modal.open()
      if (result) this.onGetData(this.filters)
    },
    async toUploadUsersActivityFile() {
      const modal = await createUploadUsersActivityFile(this)
      const result = await modal.open()
      if (result) this.onGetData(this.filters)
    },
    async toUploadPermissionGroupsFile() {
      const modal = await createUploadPermissionGroupsFile(this)
      const result = await modal.open()
      if (result) this.onGetData(this.filters)
    },

    async onSelectExportMenu(data) {
      switch (data.id) {
        case MENU_EXPORT.USERS_ACTIVITY.id: {
          await this.toExportUsersActivity()
          break
        }

        default:
      }
    },

    async toExportUsersActivity() {
      const modal = await createExportUsersActivityModal(this)
      await modal.open().finally(() => modal.close())
    },

    async onSuAs(item) {
      const api = new AuthServiceApi()
      try {
        await api.loginAs(item.username)
        this.$router.go(0)
      } catch (e) {
        this.$notice.errors(e)
      }
    },
  },
}
</script>
